import React, { memo, useEffect, useMemo } from 'react';

import produce from 'immer';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useDebounce } from 'hooks/useDebaunce';
import useHashmapConverter from 'hooks/useHashmapConverter';
import { useQueryState } from 'hooks/useQueryState';

import { useGlobalFilterDataStore } from 'store/globalFilterData.store';

import { useQueryGetMilestoneFragnet } from 'api/queries/getMilestoneFragnet.query';
import { useQueryGetSingleMilestoneFragnet } from 'api/queries/getSingleMilestoneFragnet.query';

import { IState } from 'components/common/GlobalFilterComponent/GlobalFilterComponent';
import classes from 'components/common/GlobalFilterComponent/GlobalFilterComponent.module.scss';
import { FilterLabels } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';

import { milestoneFragnetMapper } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';
import { ProgramStatusUrlParams } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramStatusSummaryReport/programStatusSummaryReportTypes';
import { Select } from 'components/DesignSystem/Select';

interface Props {
    methods?: UseFormReturn<any, any>;
    name: string;
    setState?: React.Dispatch<React.SetStateAction<IState>>;
    handleFilters?: () => void;
    overrideLatestVersionId?: number | undefined;
    skipFormSet?: boolean;
}

export const MilestoneFragnetFilter = memo(
    ({ methods, name, setState, handleFilters, overrideLatestVersionId, skipFormSet = false }: Props) => {
        //get version hashes from url params
        const { id, versionToCompareId } = useParams<ProgramStatusUrlParams>();
        // convert version hashes to string with comma between
        const hashcode = [id, versionToCompareId].filter((v): v is string => Boolean(v)).join(',');
        const { versionList: versionFromHashMap } = useHashmapConverter(hashcode);

        const latestVersionId = overrideLatestVersionId ?? versionFromHashMap[0]?.versionDbId;

        const [queryStringValue] = useQueryState('milestoneFragnet');
        const { getValueFromStore } = useGlobalFilterDataStore();
        const storeValue = getValueFromStore<string>('milestoneFragnet');

        const [filter, setFilter] = React.useState('');
        const debouncedValue = useDebounce<string>(filter, 1000);
        const { data: milestoneFragnet, isFetching } = useQueryGetMilestoneFragnet({
            latestVersion: latestVersionId,
            filter: debouncedValue,
        });

        const options = useMemo<ISelectOption<string>[]>(
            () =>
                milestoneFragnet
                    ? milestoneFragnet.map((item) => ({
                          id: item.id,
                          label: `${item.name} (ID: ${
                              item.activity_id.length > 20 ? item.activity_id.slice(0, 20) + '...' : item.activity_id
                          }) (~${item.preceding_activities} related activities)`,
                          value: item.task_version_hash_code,
                      }))
                    : [],
            [milestoneFragnet],
        );

        const formValue = methods?.getValues(name);
        const isValue = formValue || queryStringValue || storeValue;

        const { data: milestoneSingleFragnet } = useQueryGetSingleMilestoneFragnet({
            latestVersion: latestVersionId,
            taskVersionHashCode: isValue, // make sure BE can perform search by task_version_hash_code
        });

        useEffect(() => {
            if (setState) {
                if (!isValue) {
                    setState((prev) => {
                        return produce(prev, (draft) => {
                            draft.milestoneFragnet.isReady = true;
                            draft.milestoneFragnet.isEmpty = true;
                            return draft;
                        });
                    });
                }

                if (milestoneSingleFragnet && !skipFormSet) {
                    if (milestoneSingleFragnet.length > 0) {
                        methods?.setValue(name, milestoneFragnetMapper(milestoneSingleFragnet[0]).value);
                    }

                    setState((prev) => {
                        return produce(prev, (draft) => {
                            draft.milestoneFragnet.isReady = true;
                            return draft;
                        });
                    });
                }
            }
        }, [isValue, milestoneFragnet, milestoneSingleFragnet, setState]);

        const handleInputChangePrimary = (inputText, event) => {
            if (event.action !== 'input-blur' && event.action !== 'menu-close' && event.action !== 'set-value') {
                setFilter(inputText);
            }
            if (event.action === 'menu-close') {
                setFilter('');
            }
        };

        const handleChange = (value: ISelectOption<string> | null) => {
            methods?.setValue(name, value ? value.value : null);
            handleFilters && handleFilters();
        };

        const handleValue = (value) => {
            if (!value) {
                return null;
            }
            if (
                milestoneSingleFragnet &&
                milestoneSingleFragnet.length > 0 &&
                milestoneFragnetMapper(milestoneSingleFragnet[0]).value === value
            ) {
                return milestoneFragnetMapper(milestoneSingleFragnet[0]);
            }
            return milestoneFragnet?.map(milestoneFragnetMapper).find((item) => item.value === value) || null;
        };

        return (
            <div className={classes.selectWrapper}>
                <div className={classes.activityCodesSelectWrapper}>
                    <Controller
                        render={({ field }) => (
                            <Select<ISelectOption<string>>
                                {...field}
                                options={options}
                                id={'global-filter-component-fragnet-milestones'}
                                onInputChange={handleInputChangePrimary}
                                isClearable={true}
                                isLoading={isFetching}
                                onChange={handleChange}
                                value={handleValue(field.value)}
                                placeholder={'Select activity or start typing its name...'}
                                label={FilterLabels.FRAGNET_SEARCH}
                            />
                        )}
                        name={name}
                        control={methods?.control}
                    />
                </div>
            </div>
        );
    },
);
